import * as _ from 'lodash';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useContext,
} from 'react';
import produce from 'immer';

import * as Redux from '../../types/redux';
import * as Filter from '../../util/filters';
import * as QueryTS from '../../util/queryts';
import * as Requests from '../../util/requests';
import * as Section from '../../util/section';
import {useApolloClient, useDispatch, usePropsSelector} from '../hooks';
import * as RunsActions from '../runs-low/actions';
import * as RunsLowLib from '../runs-low/lib';
import * as RunsSelectors from '../runs-low/selectors';
import * as RunsTypes from '../runs-low/types';
import * as Generated from '../../generated/graphql';
import {TableMetadata} from '../../types/media';

import * as Types from './types';
import * as Context from './context';
import {useHistoryKeysQuery} from '../graphql/historyKeysQuery';
import {useWandbConfigQuery} from '../graphql/wandbConfigQuery';
import {useRunKeysQuery} from '../graphql/runKeysQuery';
import {useGatedValue} from '../hooks';
import * as ViewHooks from '../views/hooks';
import * as RunSetViewTypes from '../views/runSet/types';
import {Run} from '../../util/runs';

enum LoadingState {
  Inited,
  Loading,
  Done,
}

// It's important that this returns reference-equal results as often
// as possible, for performance of consumers. The unit tests verify
// this behavior.
export const useRunsData = (
  query: Types.Query,
  skip: boolean = false
): {loading: boolean; data: Types.Data} => {
  const dispatch = useDispatch();
  const loadingRef = useRef(LoadingState.Inited);
  const queryIDsRef = useRef([] as string[]);
  const queryIDs = queryIDsRef.current;
  const [page, setPage] = useState(1);

  if (query.page) {
    query = {
      ...query,
      page: {
        size: query.page.size * page,
      },
    };
  }

  // Unregister all queries on unmount
  const unregisterAll = useCallback(() => {
    // Unregister all with redux
    _.values(queryIDsRef.current).forEach(id =>
      dispatch(RunsActions.unregisterQuery(id))
    );
    // Clear our local IDs
    queryIDsRef.current = [];
    // The react-hooks linter wants us to put dispatch in the deps list.
    // We don't because the unit test swaps the whole store (which swaps)
    // dispatch) to do store updates, and we don't want everything to
    // clear when that happens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Unregister all queries on unmount
  useEffect(() => () => unregisterAll(), [unregisterAll]);

  const previousQueries = usePropsSelector(
    RunsSelectors.makeQueryListSelector,
    queryIDs
  );

  const queries = queryToRunsQueries(query);

  const actions: Redux.DispatchableAction[] = [];

  // If our queries have changed, compute the new query IDs, and
  // a list of actions to dispatch to redux to make the updates.
  // We track our queryIDs updates in a variable called newQueryIDs
  // so that afterward we can check if we've made changes by
  // deepEqual comparison of newQueryIDs and queryIDs.
  let newQueryIDs = [...queryIDs];

  if (!skip) {
    for (let i = 0; i < queries.length; i++) {
      if (
        i >= previousQueries.length ||
        (previousQueries[i] != null &&
          RunsLowLib.queryNeedsClear(queries[i], previousQueries[i]!))
      ) {
        if (i < previousQueries.length) {
          actions.push(RunsActions.unregisterQuery(newQueryIDs[i]));
        }
        const register = RunsActions.registerQuery(queries[i]);
        newQueryIDs[i] = register.payload.id;
        actions.push(register);
      } else {
        actions.push(
          RunsActions.updateQueryIfChanged(newQueryIDs[i], queries[i])
        );
      }
    }
    for (let i = queries.length; i < queryIDs.length; i++) {
      actions.push(RunsActions.unregisterQuery(newQueryIDs[i]));
    }
    newQueryIDs = newQueryIDs.slice(0, queries.length);
  }

  // only update queryIDs ref if we've made a change.
  if (!_.isEqual(newQueryIDs, queryIDs)) {
    queryIDsRef.current = newQueryIDs;
  }

  useEffect(() => {
    for (const action of actions) {
      dispatch(action as any);
    }
  });

  const currentlyLoading = usePropsSelector(
    RunsSelectors.makeQueriesLoadingSelector,
    queryIDsRef.current
  );
  if (currentlyLoading === true && loadingRef.current === LoadingState.Inited) {
    loadingRef.current = LoadingState.Loading;
  } else if (
    currentlyLoading === false &&
    loadingRef.current === LoadingState.Loading
  ) {
    loadingRef.current = LoadingState.Done;
  }
  const data = usePropsSelector(
    RunsSelectors.makeRunsDataSelector,
    queryIDsRef.current
  );
  const totalRuns = usePropsSelector(
    RunsSelectors.makeTotalRunsSelector,
    queryIDsRef.current
  );

  // Create a boolean instead of an object, so that useMemo below can
  // ref-equal check.
  const requestedPaging = query.page != null;

  const result = useMemo(
    () => ({
      loading: currentlyLoading,
      data: {
        ...data,
        initialLoading: loadingRef.current !== LoadingState.Done,
        loading: currentlyLoading,
        entityName: query.entityName,
        projectName: query.projectName,
        totalRuns,
        loadMore: requestedPaging ? () => setPage(page + 1) : undefined,
      },
    }),
    [
      query.entityName,
      query.projectName,
      requestedPaging,
      page,
      currentlyLoading,
      data,
      totalRuns,
    ]
  );

  return result;
};

// Give the results of the useRunsData hook, walk the loaded histories and
//     look for table-file types to load. Load them from file storage, and
//     inject the results into the histories, as if nothing ever happened.
//
// NOTE: Currently has O(n^2) behavior, because it walks the history for each
// file in the history. And maybe worse, it will trigger a rerender for each file
// in the history. We could put a debounce on actually updating results when
// new files are loaded, or batch requests etc.
//
// NOTE: This also has unbounded memory behavior, it never forgets files, and tries
// to load them no matter the size.
export const useLoadHistoryFileTables = (
  query: Types.Query,
  runsDataQuery: ReturnType<typeof useRunsData>
) => {
  const apolloClient = useApolloClient();
  const [res, setRes] = useState(runsDataQuery);
  // Track which file paths we've already started requests for, and never request
  // them again.
  const fileReqsRef = useRef<{[name: string]: boolean}>({});
  // Mapping from file path to loaded table
  const [tables, setTables] = useState<{[name: string]: TableMetadata}>({});
  const curData = runsDataQuery.data;

  // Generate requests for any files in fetched history/summary that we haven't
  // generated requests for yet.
  useEffect(() => {
    const getRunPath = (run: Types.RunWithRunsetInfo, qq: Types.Query) => {
      const runSetId = run.runsetInfo.id;
      const runSet = qq.queries.find(q => q.id === runSetId);
      if (runSet == null) {
        throw new Error('invalid state');
      }
      return {
        entityName: runSet.entityName,
        projectName: runSet.projectName,
        runName: run.name,
      };
    };
    const maybeFetchVal = (
      entityName: string,
      projectName: string,
      runName: string,
      v: any
    ) => {
      if (v != null && v._type === 'table-file' && typeof v.path === 'string') {
        if (fileReqs[v.path] == null) {
          fileReqs[v.path] = true;
          // make request
          Requests.fetchFileInfo(apolloClient, {
            entityName,
            projectName,
            runName,
            filename: v.path,
          }).then(file => {
            if (!file || file.sizeBytes === 0) {
              // TODO: We should be retrying this case, this happens when
              // the file is in history but we haven't received metadata back
              // in gorilla yet.
              return;
            }
            Requests.fetchWithRetry(file.directUrl, {
              onSuccess: loadedFile => {
                let parsed: any;
                try {
                  parsed = JSON.parse(loadedFile);
                } catch {
                  // TODO: This is a hacky way to get out of an infinite loading spinner when the
                  // retrieved JSON is invalid. We should handle this downstream somewhere and
                  // inform the user that the data is bad.
                  console.error(`failed to parse data for ${v.path}`);
                  setTables(curTables => ({
                    ...curTables,
                    [v.path]: {_type: 'invalid'},
                  }));
                  return;
                }
                if (parsed.columns != null && parsed.data != null) {
                  setTables(curTables => ({
                    ...curTables,
                    [v.path]: {_type: 'table', ...parsed},
                  }));
                }
              },
            });
          });
        }
      }
    };
    // Walk summary
    const fileReqs = fileReqsRef.current;
    for (const r of curData.filtered) {
      const {entityName, projectName, runName} = getRunPath(r, query);
      _.forEach(r.summary, v =>
        maybeFetchVal(entityName, projectName, runName, v)
      );
    }
    // Walk history
    for (const h of curData.histories.data) {
      const {entityName, projectName, runName} = getRunPath(
        curData.filteredRunsById[h.name],
        query
      );
      for (const row of h.history) {
        _.forEach(row, v => maybeFetchVal(entityName, projectName, runName, v));
      }
    }
  }, [apolloClient, query, curData]);

  // Whenever curData or tables change, recreate injected data.
  useEffect(() => {
    const updateRow = (row: {[key: string]: any}) => {
      let didUpdate = false;
      for (const k of _.keys(row)) {
        const v = row[k];
        if (
          v != null &&
          v._type === 'table-file' &&
          typeof v.path === 'string'
        ) {
          didUpdate = true;
          const table = tables[v.path];
          if (table != null) {
            row[k] = tables[v.path];
          }
        }
      }
      return didUpdate;
    };
    setRes(curQuery => {
      return produce(runsDataQuery, draft => {
        const data = draft.data;
        for (const r of data.filtered) {
          if (updateRow(r.summary)) {
            // Need to update filteredRunsById too
            data.filteredRunsById[r.name] = r;
          }
        }
        for (const h of data.histories.data) {
          for (const row of h.history) {
            updateRow(row);
          }
        }
      });
    });
  }, [runsDataQuery, tables]);

  // Only update the result when we don't have any table-file entries in
  // summary or history, meaning everything has been loaded and injected.
  // Downstream code (Vega) expects history rows not to be updated for
  // a given query, which is reasonable.
  return useGatedValue(
    res,
    curRes =>
      curRes.data.histories.data.find(hist =>
        hist.history.find(row =>
          Object.values(row).find(v => v != null && v._type === 'table-file')
        )
      ) == null &&
      curRes.data.filtered.find(run =>
        Object.values(run.summary).find(
          v => v != null && (v as any)._type === 'table-file'
        )
      ) == null
  );
};

function singleQueryToRunsQueries(
  outerQuery: Types.Query,
  query: QueryTS.SingleQuery
): RunsTypes.Query {
  return {
    runsetId: query.id,
    runsetName: query.name,
    entityName: query.entityName,
    projectName: query.projectName,
    grouping: query.grouping || [],
    filters: query.filters,
    sort: query.sort,
    limit: outerQuery.page != null ? outerQuery.page.size : 10,
    enableBasic: true,
    enableHistoryKeyInfo: outerQuery.historyKeyInfo,
    enableSystemMetrics: true,
    historySpecs: outerQuery.historySpecs,
    fullConfig: outerQuery.fullConfig,
    configKeys: outerQuery.configKeys,
    fullSummary: outerQuery.fullSummary,
    summaryKeys: outerQuery.summaryKeys,
    wandbKeys: outerQuery.wandbKeys,
  };
}

export function queryToRunsQueries(inputQuery: Types.Query): RunsTypes.Query[] {
  if (inputQuery.disabled) {
    return [];
  }
  return inputQuery.queries.map(q => singleQueryToRunsQueries(inputQuery, q));
}

export function useRunsQueryContext(): Context.RunsQueryContext {
  const context = useContext(Context.RunQueryContext);
  if (context.entityName == null || context.projectName == null) {
    throw new Error('context not initialized');
  }
  return context as Context.RunsQueryContext;
}

export function useRunSetsQuery(
  runSetRefs: RunSetViewTypes.Ref[],
  includeDisabled: boolean = true
) {
  const runSets = ViewHooks.useWholeArray(runSetRefs);
  const context = useRunsQueryContext();
  return useMemo(
    () =>
      ({
        id: 'project-page-not-used',
        entityName: context.entityName,
        projectName: context.projectName,
        runName: context.runId,
        filters: context.mergeFilters || Filter.EMPTY_FILTERS,
        sort: QueryTS.CREATED_AT_DESC,
        runSets: Section.runSetsToRunSetQuery(
          includeDisabled ? runSets : runSets.filter(rs => rs.enabled)
        ),
      } as QueryTS.Query),
    [runSets, context, includeDisabled]
  );
}

export function useKeyInfoQuery(runSetRefs: RunSetViewTypes.Ref[]) {
  const pageQuery = useRunSetsQuery(runSetRefs, false);
  const filters = Filter.And([
    pageQuery.filters,
    pageQuery.selections || Filter.And([]),
    Filter.Or(
      (pageQuery.runSets || []).map(rs =>
        Filter.And([rs.filters, rs.selections || Filter.And([])])
      )
    ),
  ]);

  const {entityName, projectName} = QueryTS.firstEnabledProject(pageQuery);
  return useHistoryKeysQuery({
    entityName,
    projectName,
    filters,
    sort: pageQuery.sort,
  });
}

export function useRunsWandbConfigQuery(runSetRefs: RunSetViewTypes.Ref[]) {
  const pageQuery = useRunSetsQuery(runSetRefs, false);
  const filters = Filter.And([
    pageQuery.filters,
    pageQuery.selections || Filter.And([]),
    Filter.Or(
      (pageQuery.runSets || []).map(rs =>
        Filter.And([rs.filters, rs.selections || Filter.And([])])
      )
    ),
  ]);

  const {entityName, projectName} = QueryTS.firstEnabledProject(pageQuery);
  return useWandbConfigQuery({
    entityName,
    projectName,
    filters,
    sort: pageQuery.sort,
  });
}

export function useKeysQuery(
  runSetRefs: RunSetViewTypes.Ref[],
  types: string[] = ['number', 'string', 'boolean', 'other'],
  limit: number = 1000
) {
  const pageQuery = useRunSetsQuery(runSetRefs);
  const runKeysQuery = useRunKeysQuery({
    entityName: pageQuery.entityName,
    projectName: pageQuery.projectName,
    types,
    limit,
  });

  return runKeysQuery;
}

// This is only used to set notes, displayName, and tags. Currently you must
// pass in notes and displayName because the backend clears them if you pass undefined.
// A better approach would be to provide update run actions that work
// directly on the store, and then write a saga that watches for them.
// Or maybe we could fetch the state from redux, compute the mutation,
// do the backend mutation (to make sure it succeeds), and then
// update the store.
export function useUpdateRun() {
  const [updateRun] = Generated.useUpsertRunMutation();
  const dispatch = useDispatch();
  return useCallback(
    (serverID: string, runID: string, vars: RunsTypes.UpdateRunVars) => {
      dispatch(RunsActions.updateRun(runID, vars));
      return updateRun({variables: {id: serverID, ...vars}});
    },
    [dispatch, updateRun]
  );
}

export function useUpdateRunTags() {
  const [updateRun] = Generated.useUpsertRunMutation();
  const dispatch = useDispatch();
  return useCallback(
    (run: Run, tagNames: string[]) => {
      updateRun({
        variables: {
          id: run.id,
          tags: tagNames,
          // Sending undefined values for displayName or notes clears them, so
          // we always need to send both.
          displayName: run.displayName,
          notes: run.notes,
        },
      }).then(response => {
        // Update run tags in redux store
        const newTags = response.data?.upsertBucket?.bucket?.tags;
        if (newTags != null) {
          dispatch(RunsActions.updateRunTags({[run.name]: newTags}));
        }
      });
    },
    [dispatch, updateRun]
  );
}

export function useBulkUpdateRunTags() {
  const [modifyRuns] = Generated.useModifyRunsMutation();
  const dispatch = useDispatch();
  return useCallback(
    (variables: {
      entityName: string;
      projectName: string;
      filters: string;
      addTags?: string[];
      removeTags?: string[];
    }) => {
      return modifyRuns({variables}).then(response => {
        // Update run tags in redux store
        const updatedRuns = response.data?.modifyRuns?.runsSQL;
        if (updatedRuns != null) {
          const tagMap: {[runName: string]: Generated.Tag[]} = {};
          updatedRuns.forEach(r => {
            if (r?.name != null) {
              tagMap[r.name] = r?.tags;
            }
          });
          dispatch(RunsActions.updateRunTags(tagMap));
        }
      });
    },
    [dispatch, modifyRuns]
  );
}
